// app.jsx — top-level shell: state, persistence, navigation
// Real web funnel — no prototype chrome, no phone frame, responsive.

import { createElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';

// Side-effect import: mirrors config to window.OPAL_CONFIG. Small + synchronous.
import './config.js';

// Opal Funnel Studio shim — gated import. The shim only does anything
// when the URL includes `?_studio=1` (the studio iframes the funnel
// with that flag). Side-effect import; tiny module, no runtime cost on
// a normal funnel load.
import './studio-draft-shim.js';
import { calcScreenYears, calcReclaimedYears, detoxTier } from './math.js';
import { applyFunnelToScreens, resolveCurrentFunnel } from './funnels/index.js';
import {
  ExperimentProvider,
  getVariant,
  isExperimentClientReady,
  peekVariant,
  subscribeToExperiments,
} from './lib/experiments';

// Detox variant. The "Social Media Detox Plan" narrative (tier reveal,
// time-of-day capture, Paladin-style acknowledgements, sneak peek, relocated
// social proof) is scoped to the /detox funnel — not enabled on /2026,
// /screentime, /adhd, or /families. window.__detoxVariant is set after the
// active funnel resolves below, so screens can read it during render.

// integrations.js (Amplitude + Meta Pixel + RC SDK, ~70 KB of the bundle) is
// lazy-loaded — see installIntegrationsShim() and the useEffect in <App/>.
import PHASE1 from './screens-phase1.jsx';

// Phases 2–9 code-split into async chunks. The user moves through 55 screens
// over minutes; loading the whole funnel up front bloats the welcome-screen
// critical path with checkout + redemption code they may never reach.
const loadPhase = (n) => {
  switch (n) {
    case 2: return import('./screens-phase2.jsx');
    case 3: return import('./screens-phase3.jsx');
    case 4: return import('./screens-phase4.jsx');
    case 5: return import('./screens-phase5.jsx');
    case 6: return import('./screens-phase6.jsx');
    case 7: return import('./screens-phase7.jsx');
    case 8: return import('./screens-phase8.jsx');
    case 9: return import('./screens-phase9.jsx');
    default: return Promise.resolve({ default: [] });
  }
};

const activeFunnel = resolveCurrentFunnel();
const isFamilyFunnel = activeFunnel.slug === 'families';
const isAdhdFunnel = activeFunnel.slug === 'adhd';
const isSchoolsFunnel = activeFunnel.slug === 'schools-promo';
const isDetoxFunnel = activeFunnel.slug === 'detox';
const isEagerSingleChunkFunnel = isFamilyFunnel || isAdhdFunnel || isSchoolsFunnel;

// Mirror the active funnel's detox state to a global flag so screens can
// branch their copy without prop-drilling — `window.__detoxVariant === 'v1'`
// means "render detox-plan copy" (only true on /detox today).
//
// Escape hatch: /detox?detox=v0 forces v0 so flagged users can be pulled
// back to the previous strings without a code deploy. Off /detox, the flag
// is always 'v0'.
const detoxEscapeHatch = typeof window !== 'undefined'
  && new URLSearchParams(window.location.search).get('detox') === 'v0';
if (typeof window !== 'undefined') {
  window.__detoxVariant = (isDetoxFunnel && !detoxEscapeHatch) ? 'v1' : 'v0';
}
// Resolver lets the funnel filter honor screen.requiredVariant gates. Reads
// the current assignment WITHOUT firing exposure (exposure is fired from the
// rendered screens themselves). Returns 'off' before the experiment SDK
// resolves — gated screens default to excluded (control behavior) in that
// window. The useEffect below re-runs the filter once the SDK is ready so
// treatment users who landed before SDK fetch still get their hero.
const variantResolver = (flag) => peekVariant(flag)?.value;
// Drop screens that don't match the active funnel's detox variant. Screens
// may declare `variant: 'detox_v1' | 'pre_detox'`; unmarked screens always
// render. On /detox the pre-detox-only s4 position drops out and the detox
// additions stay; on every other funnel the detox additions drop out so
// /2026, /screentime, /adhd, /families remain untouched.
function filterByDetoxVariant(screens) {
  if (isDetoxFunnel) return screens.filter((s) => s.variant !== 'pre_detox');
  return screens.filter((s) => s.variant !== 'detox_v1');
}
const applyActiveFunnel = (screens) =>
  filterByDetoxVariant(applyFunnelToScreens(screens, activeFunnel, { variantResolver }));
const isCanonicalFunnel = activeFunnel.slug === 'screentime';
const isStudioThumbnail = typeof window !== 'undefined'
  && new URLSearchParams(window.location.search).get('_studio_thumb') === '1';
const cursorStorageKey = isCanonicalFunnel ? 'opal.cursor' : `opal.funnel.${activeFunnel.slug}.cursor`;
const stateStorageKey = isCanonicalFunnel ? 'opal.state' : `opal.funnel.${activeFunnel.slug}.state`;

const analyticsVariant = activeFunnel.analyticsVariant || activeFunnel.slug;

if (typeof window !== 'undefined') {
  window.__opalFunnel = activeFunnel;
  try { sessionStorage.setItem('opal.funnelVariant', analyticsVariant); } catch {}
}

// ──────────────────────────────────────────────────────────────
// OpalTrack / OpalRC shims — installed synchronously so screens can call
// window.OpalTrack.capture() and window.OpalRC.fetchPackages() before the
// integrations bundle has loaded. Track calls buffer; RC calls forward to
// the real SDK once integrations.js resolves.
// ──────────────────────────────────────────────────────────────
function installIntegrationsShim() {
  if (typeof window === 'undefined') return null;
  if (window.__opalShimInstalled) return window.__opalIntegrationsPromise || null;
  window.__opalShimInstalled = true;

  const trackQueue = [];
  const noop = () => {};
  window.OpalTrack = {
    capture:             (...a) => { trackQueue.push(['capture', a]); },
    identify:            (...a) => { trackQueue.push(['identify', a]); },
    setCurrentStep:      (...a) => { trackQueue.push(['setCurrentStep', a]); },
    setCurrentScreen:    (...a) => { trackQueue.push(['setCurrentScreen', a]); },
    trackPurchase:       (...a) => { trackQueue.push(['trackPurchase', a]); },
    trackPurchaseFailed: (...a) => { trackQueue.push(['trackPurchaseFailed', a]); },
    setUserProperty:     (...a) => { trackQueue.push(['setUserProperty', a]); },
    setConsent:          (...a) => { trackQueue.push(['setConsent', a]); },
    log:  noop,
    warn: noop,
    __q: trackQueue,
  };

  // Lazy-load integrations.js on first RC access so deep-linked checkout
  // (cursor already on phase 8 at refresh) still works. Otherwise the idle
  // callback in <App/> primes it shortly after first paint.
  let promise = null;
  const ensure = () => (promise = promise || import('./integrations.js'));
  window.__opalIntegrationsPromise = ensure;

  const fwd = (method) => (...args) => ensure().then(() => window.OpalRC[method](...args));
  window.OpalRC = {
    initRC:                fwd('initRC'),
    attachIdentity:        fwd('attachIdentity'),
    fetchPackages:         fwd('fetchPackages'),
    purchasePackageById:   fwd('purchasePackageById'),
    purchaseRcPackage:     fwd('purchaseRcPackage'),
    getTargetOffering:     fwd('getTargetOffering'),
    getOrCreateAppUserId:  () => null,
    lastPurchase:          () => null,
    // buildHandoffUrl needs the real OpalRC; return a promise that resolves to
    // an attribution-decorated generic handoff URL. RevenueCat redemption URLs
    // should bypass this and be opened raw.
    buildHandoffUrl:       (baseUrl) => ensure().then(() => window.OpalRC.buildHandoffUrl(baseUrl)),
    isSandbox:             () => !!(window.OPAL_CONFIG && window.OPAL_CONFIG.USE_SANDBOX),
  };
  return ensure;
}

const ensureIntegrations = installIntegrationsShim();

// Global spinner keyframe for the phase-load skeleton. Phases that need their
// own animation keyframes inject them on mount; this one runs before any phase
// is guaranteed to have loaded.
if (typeof document !== 'undefined' && !document.getElementById('opal-app-kf')) {
  const s = document.createElement('style');
  s.id = 'opal-app-kf';
  s.textContent = '@keyframes opalSpin { to { transform: rotate(360deg); } }';
  document.head.appendChild(s);
}

// PHASE_STEP is the coarse phase label used to detect phase-boundary crossings
// (Quiz Started / Quiz Completed / Loading Completed). Not used for per-screen
// auto-fire — the V4 plan collapses all per-screen Step Viewed events to a
// small milestone set (see MILESTONE_EVENT below).
const PHASE_STEP = {
  1: 'Quiz',
  2: 'Quiz',
  3: 'Quiz',
  4: 'Quiz',
  5: 'Features',
  6: 'Loading',
  7: 'PlanReveal',
  8: 'Paywall',
  9: 'Redemption',
};
function stepFor(screen) {
  return screen ? (PHASE_STEP[screen.phase] || 'Step') : 'Step';
}

// Milestone-driven event dispatch. Each entry in MILESTONE_EVENT is fired when
// the cursor crosses a screen with the matching `milestone` tag. This is
// version-independent: screens can be added, removed, or re-ordered as long as
// the semantic checkpoint still exists somewhere.
//
// Screens with `milestone` in MANUAL_MILESTONE_FIRE are skipped here — they
// fire the milestone event from their own render (typically because they need
// to wait for async data before firing, e.g. the paywall waits on RC pricing).
const MILESTONE_EVENT = {
  projection_viewed: {
    name: 'Web Funnel - Projection Viewed',
    props: (state) => ({
      lifetime_years:  calcScreenYears(state.dailyHours, state.age),
      reclaimed_years: calcReclaimedYears(state.dailyHours, state.age),
      daily_hours:     state.dailyHours,
      age:             state.age,
      tier:            detoxTier(state.dailyHours).id,
    }),
  },
  plan_ready: {
    name: 'Web Funnel - Plan Ready Viewed',
    props: (state) => ({ tier: detoxTier(state.dailyHours).id }),
  },
  email_gate: {
    name: 'Web Funnel - Email Gate Viewed',
    props: () => ({}),
  },
  handoff: {
    name: 'Web Funnel - Handoff Viewed',
    props: (state) => ({ has_redeem_link: !!state.redeemUrl }),
  },
  // New v1 milestones for the detox-plan arc.
  acknowledgement_viewed: {
    name: 'Web Funnel - Acknowledgement Viewed',
    props: (state, screen) => ({
      which: screen?.acknowledgement || null,
      tier:  detoxTier(state.dailyHours).id,
    }),
  },
  summary_viewed: {
    name: 'Web Funnel - Summary Viewed',
    props: (state) => ({
      tier:           detoxTier(state.dailyHours).id,
      daily_hours:    state.dailyHours,
      app_count:      (state.heavyApps || []).length,
      scroll_windows: state.scrollWindows || [],
      sacrifice:      state.sacrifice,
      lock_style:     state.lockStyle,
    }),
  },
  detox_plan_preview_viewed: {
    name: 'Web Funnel - Detox Plan Preview Viewed',
    props: (state) => ({
      tier:           detoxTier(state.dailyHours).id,
      scroll_windows: state.scrollWindows || [],
      heavy_apps:     state.heavyApps || [],
      sacrifice:      state.sacrifice,
      age:            state.age,
    }),
  },
  // Family-specific: fires when the diagnosis card renders for the parent's
  // selected branch. familyBranch is the derived branch id from familyProfile.
  family_diagnosis: {
    name: 'Web Funnel - Family Diagnosis Viewed',
    props: (state) => ({
      family_branch: state.familyBranch || null,
      conflict_window: state.familyConflictWindow || null,
      family_stage: state.familyStage || null,
      primary_target: state.familyPrimaryTarget || null,
    }),
  },
  // paywall fires from screens-phase8 / screens-family after packages load.
};
const MANUAL_MILESTONE_FIRE = new Set(['paywall']);

function parseUtm() {
  const out = {};
  try {
    const params = new URLSearchParams(location.search);
    params.forEach((v, k) => { if (/^utm_/i.test(k)) out[k] = v; });
  } catch {}
  return out;
}

// localStorage TTL wrapper. State is wrapped as { v, expiresAt }; expired reads
// return null and clear the slot so a new session starts clean.
const STATE_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days
function loadState(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && 'v' in parsed && 'expiresAt' in parsed) {
      if (Date.now() > parsed.expiresAt) { localStorage.removeItem(key); return null; }
      return parsed.v;
    }
    return parsed; // legacy unwrapped — accepted once, re-wrapped on next save
  } catch { return null; }
}
function saveState(key, v) {
  try { localStorage.setItem(key, JSON.stringify({ v, expiresAt: Date.now() + STATE_TTL_MS })); } catch {}
}

const DEFAULT_STATE = {
  age: null,
  funnelSlug: 'screentime',
  funnelName: 'Screen Time Funnel',
  funnelVariant: 'screentime_v1',
  dailyHours: 8,
  dailyHoursRange: null,
  heavyApps: [],
  scrollWindows: [],
  habits: [],
  usePlan: null,
  guilt: null,
  reason: null,
  sacrifice: null,
  thinker: null,
  revengeBedtime: null,
  lockStyle: null,
  responseStyle: null,
  preferVisual: null,
  ready: null,
  email: '',
  firstName: '',
  loaderQ1: null,
  loaderQ2: null,
  selectedPackageId: null,
  redeemUrl: null,
  redemptionLinkMissing: false,
  purchasedPlan: null,
  purchaseValue: null,
  paywallDeadline: null,
  bin16: null, bin17: null, bin18: null, bin19: null, bin20: null,
  costValidation: null,
  prof26: null, prof27: null, prof28: null, prof29: null, prof30: null,
  feat31: null, feat32: null, feat33: null, feat35: null, feat36: null,
  feat37: null, feat38: null, feat39: null, feat40: null, feat_42: null,
};

function App() {
  // SCREENS is a growing array: phase 1 is eager, phases 2–9 append as their
  // chunks resolve. Total size is unknown until the user approaches a phase
  // boundary, so cursor/progress are bounded by the current length with a soft
  // upper bound of 55 for progress display (adjusted once real total is known).
  //
  // Family + ADHD exception: each is a single ~20-screen flow loaded eagerly
  // (screens-family.jsx, screens-adhd.jsx). Phase 2–9 chunks aren't relevant
  // for them — those are the canonical screen-time funnel.
  const [screens, setScreens] = useState(() =>
    isEagerSingleChunkFunnel ? [] : applyActiveFunnel(PHASE1),
  );
  const loadedPhasesRef = useRef(new Set(isEagerSingleChunkFunnel ? [] : [1]));
  const loadingPhasesRef = useRef(new Map());
  const familyLoadedRef = useRef(false);
  const adhdLoadedRef = useRef(false);
  const schoolsLoadedRef = useRef(false);
  // Phase modules cached so we can re-apply applyActiveFunnel when the
  // experiment SDK resolves variants (see useEffect below). Without this, a
  // user on /2026 who's been assigned to a variant-gated screen would see
  // their hero screen filtered out on first render and never reinserted.
  const phaseModulesRef = useRef(new Map(isEagerSingleChunkFunnel ? [] : [[1, PHASE1]]));

  const ensurePhasesUpTo = useCallback(async (phaseNum) => {
    if (isEagerSingleChunkFunnel) return;
    for (let p = 2; p <= Math.min(phaseNum, 9); p++) {
      if (loadedPhasesRef.current.has(p)) continue;
      if (!loadingPhasesRef.current.has(p)) {
        const promise = loadPhase(p).then((mod) => {
          loadedPhasesRef.current.add(p);
          const moduleScreens = mod.default || [];
          phaseModulesRef.current.set(p, moduleScreens);
          setScreens((prev) => prev.concat(applyActiveFunnel(moduleScreens)));
        });
        loadingPhasesRef.current.set(p, promise);
      }
      await loadingPhasesRef.current.get(p);
    }
  }, []);

  // Family funnel loader. Single chunk, single load, not coupled to phase
  // numbers. Runs once on mount when the active funnel is /families.
  useEffect(() => {
    if (!isFamilyFunnel || familyLoadedRef.current) return;
    familyLoadedRef.current = true;
    let cancelled = false;
    import('./screens-family.jsx').then((mod) => {
      if (cancelled) return;
      setScreens(applyActiveFunnel(mod.default || []));
    });
    return () => { cancelled = true; };
  }, []);

  // ADHD funnel loader. Mirrors families: single eager chunk on mount.
  // screens-adhd.jsx already bundles the canonical s49 paywall + s53 handoff,
  // so this load gives the funnel its full 20 screens in one go.
  useEffect(() => {
    if (!isAdhdFunnel || adhdLoadedRef.current) return;
    adhdLoadedRef.current = true;
    let cancelled = false;
    import('./screens-adhd.jsx').then((mod) => {
      if (cancelled) return;
      setScreens(applyActiveFunnel(mod.default || []));
    });
    return () => { cancelled = true; };
  }, []);

  // Schools loader. /schools-promo is a standalone two-screen one-pager with
  // no shared screens — screens-schools.jsx is the whole funnel.
  useEffect(() => {
    if (!isSchoolsFunnel || schoolsLoadedRef.current) return;
    schoolsLoadedRef.current = true;
    let cancelled = false;
    import('./screens-schools.jsx').then((mod) => {
      if (cancelled) return;
      setScreens(applyActiveFunnel(mod.default || []));
    });
    return () => { cancelled = true; };
  }, []);

  const [cursor, setCursor] = useState(() => {
    const stored = Number(localStorage.getItem(cursorStorageKey));
    // Can't validate against total length yet — phases load async. Clamp to
    // stored or 0; later effect will grow screens as needed.
    return Number.isFinite(stored) && stored >= 0 ? stored : 0;
  });
  useEffect(() => { localStorage.setItem(cursorStorageKey, String(cursor)); }, [cursor]);

  const [state, setState] = useState(() => {
    const v = loadState(stateStorageKey);
    return {
      ...DEFAULT_STATE,
      ...(v || {}),
      // Funnel-level defaults override stored state — they're intrinsic to
      // the funnel (e.g. /adhd seeds sacrifice='attention' so s9/s10 render
      // ADHD-flavoured projection-copy when the shared s15 picker is skipped).
      ...(activeFunnel.defaults || {}),
      funnelSlug: activeFunnel.slug,
      funnelName: activeFunnel.name,
      funnelVariant: activeFunnel.analyticsVariant,
    };
  });
  useEffect(() => { saveState(stateStorageKey, state); }, [state]);

  // Growth loop: if the cursor points past what we've loaded, pull in phases
  // sequentially until we have the screen. Also prefetch one phase ahead so
  // forward navigation never hits a skeleton on fast networks.
  // Skipped for the eager single-chunk funnels — they load their full screen
  // list in one chunk (families, adhd).
  useEffect(() => {
    if (isEagerSingleChunkFunnel) return;
    let cancelled = false;
    (async () => {
      while (!cancelled && cursor >= screens.length && loadedPhasesRef.current.size < 9) {
        const nextPhase = Math.max(...loadedPhasesRef.current) + 1;
        if (nextPhase > 9) break;
        await ensurePhasesUpTo(nextPhase);
      }
      // Prefetch next phase on idle if we're within 3 screens of its boundary.
      if (!cancelled && screens.length > 0 && cursor >= screens.length - 3) {
        const nextPhase = Math.max(...loadedPhasesRef.current) + 1;
        if (nextPhase <= 9) ensurePhasesUpTo(nextPhase);
      }
    })();
    return () => { cancelled = true; };
  }, [cursor, screens.length, ensurePhasesUpTo]);

  // Defer the full integrations.js (Amplitude SDK + Meta Pixel bootstrap + RC
  // module) past first paint. The shim installed at module load already
  // handles early calls by queueing track events and forwarding RC calls.
  useEffect(() => {
    if (!ensureIntegrations) return;
    const kick = () => ensureIntegrations();
    if ('requestIdleCallback' in window) {
      const id = window.requestIdleCallback(kick, { timeout: 1500 });
      return () => window.cancelIdleCallback && window.cancelIdleCallback(id);
    }
    const t = setTimeout(kick, 0);
    return () => clearTimeout(t);
  }, []);

  // Screen state is accessed via ref so the cursor effect only runs when the
  // cursor or screens array change — not on every patch() that updates state.
  // Otherwise milestone events would re-fire when the user answers a question
  // on the current screen.
  const stateRef = useRef(state);
  stateRef.current = state;
  // screensRef + cursorRef let the next/back callbacks read the current screen
  // and cursor at click time without forcing the callbacks to recompute on
  // every cursor change (which would defeat their useCallback memoization and
  // re-bind every onNext/onBack prop in every screen on every render).
  const screensRef = useRef(screens);
  screensRef.current = screens;
  const cursorRef = useRef(cursor);
  cursorRef.current = cursor;

  // Re-filter all loaded phases once the experiment SDK resolves. Handles the
  // race where /2026 paints before variants are fetched: s0_hero is filtered
  // out on first apply (peekVariant returns 'off'), so a treatment user would
  // land on s1 as if they were control. When variants arrive we recompute and
  // replace the screens array — but only if the user is still on cursor 0.
  // If they've already advanced past s1, leave them alone (no teleporting).
  const sdkRefilteredRef = useRef(false);
  // Reactive twin of sdkRefilteredRef so the render can show a black
  // placeholder at cursor 0 while the experiment SDK is mid-fetch. Without
  // this, treatment users on /2026 briefly see s1 paint, then React swaps
  // to s0_hero once the SDK resolves — the "flash" the team saw at ~500-
  // 1000 ms. Cost: control users on /2026 trade an instant s1 for ~500 ms
  // of dark loader; product called this trade favourable.
  const [experimentSdkReady, setExperimentSdkReady] = useState(() => isExperimentClientReady());
  useEffect(() => {
    if (isEagerSingleChunkFunnel) return; // family/adhd don't use variant gates today
    if (sdkRefilteredRef.current) return;
    const tryRefilter = () => {
      if (sdkRefilteredRef.current) return;
      if (!isExperimentClientReady()) return;
      sdkRefilteredRef.current = true;
      // Funnel-level A/B test: on /2026, fire the funnel_detox_vs_2026
      // experiment and redirect the detox arm to /detox BEFORE any Welcome
      // Viewed event fires. The 2026-funnel arm (and `off`) stay on /2026.
      // Direct visits to /detox skip this branch entirely (no exposure, no
      // analytics noise). Variant values come from the Amplitude flag config
      // ('2026-funnel' | 'detox-funnel' | 'off') — not the generic
      // control/treatment names.
      // Cursor=0 only — if the user has already advanced, leave them alone.
      if (activeFunnel.slug === '2026' && cursorRef.current === 0) {
        const variant = getVariant('funnel_detox_vs_2026')?.value;
        if (variant === 'detox-funnel') {
          const search = window.location.search || '';
          const hash   = window.location.hash || '';
          window.location.replace(`/detox${search}${hash}`);
          return; // intentionally leave experimentSdkReady=false so the loader
                  // skeleton stays up until the navigation actually happens —
                  // no /2026 screen ever paints for the detox arm.
        }
      }
      setExperimentSdkReady(true);
      if (cursorRef.current !== 0) return; // user already advanced; honor their flow
      const phases = Array.from(phaseModulesRef.current.keys()).sort((a, b) => a - b);
      const refiltered = phases.flatMap((p) => applyActiveFunnel(phaseModulesRef.current.get(p) || []));
      setScreens(refiltered);
    };
    if (isExperimentClientReady()) { tryRefilter(); return; }
    const unsubscribe = subscribeToExperiments(tryRefilter);
    // Fallback timeout: in local dev the Experiment SDK may never resolve
    // (no deployment key, offline, etc.). If it hasn't landed within 1.2s
    // we give up and render the funnel as if the user is in the control
    // variant — no exposure fires, no redirect happens. In prod the SDK
    // typically resolves under 100ms so this branch is never hit.
    const fallback = setTimeout(() => {
      if (sdkRefilteredRef.current) return;
      sdkRefilteredRef.current = true;
      setExperimentSdkReady(true);
      if (cursorRef.current !== 0) return;
      const phases = Array.from(phaseModulesRef.current.keys()).sort((a, b) => a - b);
      const refiltered = phases.flatMap((p) => applyActiveFunnel(phaseModulesRef.current.get(p) || []));
      setScreens(refiltered);
    }, 1200);
    return () => { clearTimeout(fallback); unsubscribe?.(); };
  }, []);
  // Once-per-session guards so back-navigation doesn't re-fire the boundary
  // events. `quizStartedRef` resets on Quiz exit so a post-Loading re-entry
  // (rare) still fires a fresh Quiz Started. Welcome Viewed uses sessionStorage
  // so a tab reload doesn't re-fire (each new tab is a fresh session).
  const quizStartedRef = useRef(false);
  // Tracks the last cursor we fired events for, so the cursor effect can
  // distinguish a real navigation (cursor changed) from a same-cursor re-run
  // caused by `screens` mutating asynchronously (phase 2-9 chunks lazy-load
  // and call setScreens, which re-runs this effect with the same cursor —
  // without this guard, every async phase load re-fires Quiz Completed,
  // milestone events, and Funnel Step Viewed for the still-current screen).
  const lastFiredCursorRef = useRef(-1);

  // Hoisted above the cursor effect so setCurrentScreen() can pass a stable
  // total. `total` itself recomputes below for non-fixed funnels (the value is
  // stable per-render either way).
  const fixedScreenCount = Array.isArray(activeFunnel.screenOrder) && activeFunnel.screenOrder.length > 0
    ? activeFunnel.screenOrder.length
    : null;
  const total = fixedScreenCount || screens.length;

  // Milestone-driven telemetry. Fires the phase-boundary events (Quiz
  // Started/Completed, Loading Completed, Welcome Viewed on cursor=0) and
  // then any milestone event declared on the current screen. Screens without
  // a milestone fire `Web Funnel - Funnel Step Viewed` so per-screen drop-off
  // is sliceable in Amplitude by `screen_key` / `page_path`.
  useEffect(() => {
    const s = screens[cursor];
    if (!s) return;

    const step = stepFor(s);
    // Phase label drives the legacy `current_step` envelope field.
    window.OpalTrack?.setCurrentStep?.(step);
    // Screen object drives the new envelope: page_name, page_path, screen_key,
    // screen_headline, funnel_step_index. Must come BEFORE any capture() call
    // in this effect so those fields are attached to the events fired below.
    // Always runs (even on same-cursor re-runs) so envelope context stays
    // fresh as `screens` grows from phase chunks loading.
    window.OpalTrack?.setCurrentScreen?.(s, cursor, total);

    // Bail before firing events if cursor hasn't changed since last fire.
    // This is the core fix for async-phase-loading multi-fires: setScreens
    // re-runs this effect with the same cursor, and we don't want every
    // milestone/boundary/Funnel-Step-Viewed event to re-fire each time.
    // A real navigation (forward or back) updates cursor, which trips this
    // guard and lets the events fire normally.
    if (cursor === lastFiredCursorRef.current) return;
    lastFiredCursorRef.current = cursor;

    // Welcome Viewed at cursor=0 — fires once per browser tab session.
    // sessionStorage outlives both back-navigation within the SPA and a
    // tab-reload, but a fresh tab fires a new event (which is the right
    // behavior for a returning visitor opening the funnel again).
    if (cursor === 0) {
      let alreadyFired = false;
      try { alreadyFired = sessionStorage.getItem('opal.welcomeFired') === '1'; } catch (_) {}
      if (!alreadyFired) {
        try { sessionStorage.setItem('opal.welcomeFired', '1'); } catch (_) {}
        window.OpalTrack?.capture('Web Funnel - Welcome Viewed', {
          ...parseUtm(),
          funnel_slug: activeFunnel.slug,
          funnel_name: activeFunnel.name,
          funnel_variant: activeFunnel.analyticsVariant,
        });
      }
    }

    // Phase-boundary events. Keyed on the phase label transition so different
    // funnel versions with different screen counts per phase still fire.
    const prev = cursor > 0 ? screens[cursor - 1] : null;
    const prevStep = prev ? stepFor(prev) : null;
    if (prev) {
      if (prevStep === 'Quiz' && step !== 'Quiz') {
        window.OpalTrack?.capture('Web Funnel - Quiz Completed', { last_screen_id: prev.id });
      }
      if (prevStep === 'Loading' && step !== 'Loading') {
        window.OpalTrack?.capture('Web Funnel - Loading Completed', { last_screen_id: prev.id });
      }
    }
    // Quiz Started fires when transitioning INTO the Quiz phase — including
    // from cursor=0 (no prev). Uses a ref so back-and-forth within Quiz
    // doesn't re-fire, and returning to Quiz after Loading re-fires cleanly.
    if (step === 'Quiz' && prevStep !== 'Quiz' && !quizStartedRef.current) {
      quizStartedRef.current = true;
      window.OpalTrack?.capture('Web Funnel - Quiz Started', { first_screen_id: s.id });
    } else if (step !== 'Quiz' && prevStep === 'Quiz') {
      quizStartedRef.current = false; // reset so a later re-entry can fire again
    }

    // Milestone dispatch (skipped for screens that fire their own milestone
    // event — currently just the paywall, which waits on RC pricing).
    const milestone = s.milestone;
    const hasMilestone = milestone && MILESTONE_EVENT[milestone];
    const isManualMilestone = milestone && MANUAL_MILESTONE_FIRE.has(milestone);
    if (hasMilestone && !isManualMilestone) {
      const def = MILESTONE_EVENT[milestone];
      window.OpalTrack?.capture(def.name, def.props(stateRef.current, s));
    }

    // Default `Funnel Step Viewed` for screens that don't declare a milestone.
    // The Welcome Viewed and milestone branches above already cover the named
    // events; this fills in the long tail (~44 of 49 screens) so per-screen
    // drop-off is observable in Amplitude. cursor=0 is excluded — Welcome
    // Viewed already covers the landing page and we don't want a duplicate.
    // Manual-fire milestones (paywall) also fire their own *Viewed event from
    // the screen, so we skip the default there too.
    if (cursor !== 0 && !hasMilestone && !isManualMilestone) {
      window.OpalTrack?.capture('Web Funnel - Funnel Step Viewed', {});
    }

    requestAnimationFrame(() => {
      try { document.activeElement && document.activeElement.blur && document.activeElement.blur(); }
      catch {}
    });
  }, [cursor, screens, total]);

  const next = useCallback(() => {
    const c = cursorRef.current;
    const fromScreen = screensRef.current[c];
    const st = stateRef.current;
    if (fromScreen) {
      if (isStudioThumbnail && fromScreen.autoAdvance) return;
      // CTA Clicked fires for any user-initiated continue. Auto-advancing
      // loaders (s8, s41, s43, s45) call onNext() programmatically when their
      // timer ends — those declare `autoAdvance: true` and skip this event.
      if (!fromScreen.autoAdvance) {
        window.OpalTrack?.capture('Web Funnel - CTA Clicked', {
          cta_label: fromScreen.ctaLabel || 'Continue',
        });
      }
      // Quiz Step Answered fires once per continue when the screen declares
      // `answerField`. The value comes from state[answerField] — captures the
      // FINAL committed answer, ignoring intermediate taps if the user changed
      // their mind. Skipped fires when the screen allows continue with no
      // answer (only s6's "I don't know" path today, instrumented in-screen).
      // Screens with skip paths that DEFAULT a value before continue (s6 sets
      // dailyHours=5 on "I don't know") set window.__opalSkipNextAnswered to
      // suppress the Answered fire — otherwise the default would be miscounted
      // as a real answer.
      const skipFlag = (typeof window !== 'undefined') && window.__opalSkipNextAnswered;
      if (skipFlag && typeof window !== 'undefined') {
        window.__opalSkipNextAnswered = false;
      }
      if (fromScreen.answerField && !skipFlag) {
        const answer = st[fromScreen.answerField];
        const hasAnswer = Array.isArray(answer)
          ? answer.length > 0
          : (answer != null && answer !== '');
        if (hasAnswer) {
          window.OpalTrack?.capture('Web Funnel - Quiz Step Answered', {
            question_key: fromScreen.answerField,
            answer_value: answer,
          });
          // Mirror every quiz answer onto the user record so Amplitude's User
          // Properties tab + cohort builder can segment by it (not just the
          // event stream). Keyed by `answerField` — screens that need a more
          // human-readable property name (e.g. age_range, heavy_apps,
          // onboarding_*) keep their explicit setUserProperty calls; this just
          // guarantees every answer also lands as a user property by default.
          window.OpalTrack?.setUserProperty?.(fromScreen.answerField, answer);
        }
      }
    }
    setCursor((cur) => fixedScreenCount ? Math.min(cur + 1, fixedScreenCount - 1) : cur + 1); // phase loader handles out-of-bounds growth for full funnels
    requestAnimationFrame(() => {
      const el = document.querySelector('.funnel-frame');
      if (el) el.scrollTop = 0;
    });
  }, [fixedScreenCount]);

  const back = useCallback(() => {
    const c = cursorRef.current;
    const fromScreen = screensRef.current[c];
    const toScreen = c > 0 ? screensRef.current[c - 1] : null;
    if (fromScreen) {
      window.OpalTrack?.capture('Web Funnel - Back Clicked', {
        from_screen_id: fromScreen.id,
        to_screen_id: toScreen?.id || null,
      });
    }
    setCursor((cur) => Math.max(cur - 1, 0));
    requestAnimationFrame(() => {
      const el = document.querySelector('.funnel-frame');
      if (el) el.scrollTop = 0;
    });
  }, []);
  const goto = useCallback((i) => setCursor(Math.max(0, i)), []);

  if (typeof window !== 'undefined') {
    window.__funnelNext = next;
    window.__funnelCursor = cursor;
    window.__funnelTotal = total;
    window.__funnelScreen = screens[cursor];
    window.__funnelScreens = screens;
    window.__funnelGoto = goto;
    window.__opalFunnel = activeFunnel;
  }

  const screen = screens[cursor];
  // Progress uses an explicit screen-order count for path-slug funnels such as
  // /2026; full funnels keep the 55-screen soft bound while later phases load.
  const progressBase = fixedScreenCount || Math.max(total, 55);
  const progress = Math.min((cursor + 1) / progressBase, 1);
  const patch = useCallback((p) => setState((s) => ({ ...s, ...p })), []);

  // Variant-gate placeholder: at cursor 0, hold a black + spinner skeleton
  // while the experiment SDK is still fetching IF the funnel's first ordered
  // screen is currently being filtered out (because peekVariant returns 'off'
  // pre-resolve). Once the SDK lands, the refilter useEffect updates `screens`
  // and `experimentSdkReady` in the same render — no flash either way.
  // Structural check (screens[0]?.id !== screenOrder[0]) auto-handles future
  // variant-gated first screens without any code change here.
  const orderedFirstId = activeFunnel.screenOrder && activeFunnel.screenOrder[0];
  const waitingForVariantGate = (
    cursor === 0
    && !experimentSdkReady
    && !isEagerSingleChunkFunnel
    && (
      // Variant-gated first screen — e.g. a detox_v1 screen filtered out
      // pre-resolve; show the loader so we don't paint the wrong screen.
      (orderedFirstId && screens.length > 0 && screens[0]?.id !== orderedFirstId)
      // /2026 entry — wait for funnel_detox_vs_2026 before painting so
      // treatment users get redirected to /detox WITHOUT first flashing
      // the /2026 hero.
      || activeFunnel.slug === '2026'
    )
  );

  return (
    <>
      <div className="funnel-root">
        <div className="funnel-frame">
          {waitingForVariantGate || !screen ? <PhaseLoadingSkeleton />
            : createElement(screen.render, {
                state,
                patch,
                onNext: next,
                onBack: cursor > 0 ? back : null,
                progress,
                goto,
                cursor,
                total,
                funnel: activeFunnel,
                funnelCopy: screen.funnelCopy || {},
                // Section pills only render on the /detox funnel. Other funnels
                // declared `section: ...` on their screens but they were always
                // a /detox UX feature; we suppress propagation here so /2026,
                // /screentime, /adhd, /families look exactly as they did before.
                section: isDetoxFunnel ? screen.section : undefined,
              })}
        </div>
      </div>
    </>
  );
}

function PhaseLoadingSkeleton() {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      minHeight: '100dvh', color: 'rgba(255,255,255,0.4)', fontSize: 14,
    }}>
      <div style={{
        width: 28, height: 28, borderRadius: '50%',
        border: '2px solid rgba(255,255,255,0.15)',
        borderTopColor: 'rgba(255,255,255,0.6)',
        animation: 'opalSpin 0.8s linear infinite',
      }} />
    </div>
  );
}

// /dev/ab-test — smoke validation route for the A/B testing system.
// Non-production only. Bypasses the funnel entirely; mounts <DevAbTest /> instead.
// See docs/ab-testing.md "Smoke test workflow".
const _isDevAbTestRoute =
  typeof window !== 'undefined' &&
  window.location?.pathname === '/dev/ab-test' &&
  __ENVIRONMENT__ !== 'production';

if (_isDevAbTestRoute) {
  // Prime the integrations bundle so Amplitude + Experiment initialize before
  // <DevAbTest /> mounts. Without this the SDK never starts (the funnel-side
  // ensure() hook never fires on this route).
  if (ensureIntegrations) ensureIntegrations();
  import('./dev-ab-test').then(({ DevAbTest }) => {
    createRoot(document.getElementById('root')).render(<DevAbTest />);
  });
} else {
  createRoot(document.getElementById('root')).render(
    <ExperimentProvider>
      <App />
    </ExperimentProvider>,
  );
}
